import { Chip, Typography } from '@material-ui/core'
import { ReactNode } from 'react'

import { formatNotificationTime } from './notificationAccess'
import { t } from './shared'
import { ResourceRow } from './types'

/**
 * One column of a resource table.
 *
 * These definitions exist because `ResourceRow` is `Record<string, unknown>`,
 * which erases a schema the GraphQL queries already carry — every field below is
 * one the query selects by name. The erasure is why the panel rendered rows as
 * `JSON.stringify`: with no field names in the type, there was nothing to build
 * columns from. Declaring them at the call site puts the schema back where it is
 * known.
 */
export interface ResourceColumn {
  label: string
  /** Grid track. Kept beside the cell so a column cannot gain a width without one. */
  width: string
  render: (row: ResourceRow) => ReactNode
}

/** A Latin identifier in an otherwise RTL row needs <bdi> or the bidi
 *  algorithm reorders it — dotted keys like `booking.created` especially. */
const code = (value: unknown): ReactNode => (
  <Typography variant="body1">
    <bdi dir="ltr">{value == null ? '—' : String(value)}</bdi>
  </Typography>
)

const text = (value: unknown): ReactNode => (
  <Typography variant="body1">{value == null ? '—' : String(value)}</Typography>
)

/** Booleans read as a state, not as the words true/false. */
const flag = (value: unknown, onLabel: string, offLabel: string): ReactNode => (
  <Chip
    size="small"
    label={t(value ? onLabel : offLabel)}
    color={value ? 'primary' : 'default'}
  />
)

const list = (value: unknown): ReactNode =>
  code(Array.isArray(value) ? value.join(', ') : value)

const when = (value: unknown): ReactNode =>
  text(value ? formatNotificationTime(String(value)) : null)

/**
 * Column sets, one per admin resource.
 *
 * Each carries the identifying and status fields — what an operator scans a
 * list for. Fields that cannot occupy a cell (CEL source, template bodies,
 * settings JSON, variable contracts) are deliberately absent: the row expander
 * shows the whole record, which is what this panel displayed before and is
 * still the only complete view.
 */
export const CHANNEL_HEALTH_COLUMNS: ResourceColumn[] = [
  { label: 'Channel', width: '3fr', render: (r) => code(r.channel) },
  { label: 'Provider', width: '4fr', render: (r) => code(r.providerKey) },
  { label: 'Status', width: '3fr', render: (r) => code(r.status) },
  { label: 'Success 1h', width: '3fr', render: (r) => text(r.successRate1h) },
  { label: 'Success 24h', width: '3fr', render: (r) => text(r.successRate24h) },
  { label: 'Pending', width: '2fr', render: (r) => text(r.pendingCount) },
  { label: 'Circuit', width: '3fr', render: (r) => code(r.circuit) }
]

export const CATEGORY_COLUMNS: ResourceColumn[] = [
  { label: 'Key', width: '6fr', render: (r) => code(r.key) },
  { label: 'Domain', width: '3fr', render: (r) => code(r.domain) },
  { label: 'Severity', width: '3fr', render: (r) => code(r.defaultSeverity) },
  { label: 'Channels', width: '4fr', render: (r) => list(r.defaultChannels) },
  {
    label: 'User mutable',
    width: '3fr',
    render: (r) => flag(r.userMutable, 'Mutable', 'Fixed')
  }
]

export const TEMPLATE_COLUMNS: ResourceColumn[] = [
  { label: 'Category', width: '6fr', render: (r) => code(r.categoryKey) },
  { label: 'Channel', width: '3fr', render: (r) => code(r.channel) },
  { label: 'Locale', width: '2fr', render: (r) => code(r.locale) },
  { label: 'Version', width: '2fr', render: (r) => text(r.version) },
  {
    label: 'Status',
    width: '3fr',
    render: (r) => flag(r.active, 'Active', 'Inactive')
  }
]

export const RULE_COLUMNS: ResourceColumn[] = [
  { label: 'Event type', width: '6fr', render: (r) => code(r.eventType) },
  { label: 'Category', width: '5fr', render: (r) => code(r.categoryKey) },
  {
    label: 'Status',
    width: '3fr',
    render: (r) => flag(r.active, 'Active', 'Inactive')
  },
  { label: 'Description', width: '6fr', render: (r) => text(r.description) }
]

export const SUBSCRIPTION_COLUMNS: ResourceColumn[] = [
  { label: 'Category', width: '6fr', render: (r) => code(r.categoryKey) },
  { label: 'Channel', width: '3fr', render: (r) => code(r.channel) },
  {
    label: 'Status',
    width: '3fr',
    render: (r) => flag(r.active, 'Active', 'Inactive')
  }
]

export const PROVIDER_CONFIG_COLUMNS: ResourceColumn[] = [
  { label: 'Channel', width: '3fr', render: (r) => code(r.channel) },
  { label: 'Provider', width: '4fr', render: (r) => code(r.providerKey) },
  {
    label: 'Status',
    width: '3fr',
    render: (r) => flag(r.active, 'Active', 'Inactive')
  },
  {
    label: 'Last verified',
    width: '4fr',
    render: (r) => when(r.lastVerifiedAt)
  }
]

/**
 * Deliveries render through the same table as the admin resources.
 *
 * They had their own table and row component, whose cells were these six
 * values hard-coded. Keeping them meant writing the responsive layout twice —
 * and the second copy would have been the one to drift. A column array is all
 * the delivery row ever was.
 */
export const DELIVERY_COLUMNS: ResourceColumn[] = [
  { label: 'Category', width: '6fr', render: (r) => code(r.categoryKey) },
  { label: 'Channel', width: '4fr', render: (r) => code(r.channel) },
  {
    label: 'Destination',
    width: '6fr',
    render: (r) => code(r.destinationMasked)
  },
  {
    label: 'Status',
    // A Chip, not text: status is what a reader scans a delivery list for, and
    // FAILED has to be findable without reading every row.
    width: '4fr',
    render: (r) => (
      <Chip
        size="small"
        label={t(String(r.status ?? ''))}
        color={r.status === 'FAILED' ? 'secondary' : 'default'}
      />
    )
  },
  { label: 'Attempt', width: '3fr', render: (r) => text(r.attemptNo) },
  {
    label: 'Updated',
    width: '5fr',
    render: (r) => when(r.completedAt ?? r.sentAt)
  }
]
